import React, { useState, useEffect } from 'react';
import stateManager from '../services/stateManager';
import { todosAPI } from '../services/api';
import { SparkleIcon } from './Icons';
import { cleanStageTitle } from '../utils/missionUtils';
import Loader from './Loader';
import Modal from './Modal';
import styles from '../styles/TodoPlanCard.module.css';

const LOADING_TIPS = [
  "Breaking your goal into achievable steps...",
  "Creating a personalised roadmap for success...",
  "Setting up your action plan...",
  "Organising tasks by priority...",
  "Preparing your next steps...",
  "Tailoring the plan to your needs...",
  "Finalising your action items..."
];

// Typical wall-clock for /api/todos/approve (new conversation + one Strapi
// write per todo). Drives the loader's countdown, not a hard timeout.
const APPROVE_ESTIMATE_MS = 25000;

/**
 * Props added for the public org landing page's scroll story (where the card
 * is a DEMO of the real component, never a live plan):
 *  - readOnly:  Edit / Accept render exactly as in the app but are inert, so
 *               nothing can reach todosAPI.approvePlan or raise the Loader
 *  - demoPress: 'edit' | 'accept' | null — paints the matching button in its
 *               pressed state, so the story can "tap" it as she scrolls
 */
function TodoPlanCard({ plan, objective, conversationId, oldObjective, onApprove, onApproved, onEdit, readOnly = false, demoPress = null }) {
  const [showCompletionModal, setShowCompletionModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editMessage, setEditMessage] = useState('');
  const [currentTipIndex, setCurrentTipIndex] = useState(0);
  const [isApproving, setIsApproving] = useState(false);

  // Cycle through loading tips — only while the approve loader is on screen.
  useEffect(() => {
    if (!isApproving) return undefined;
    const interval = setInterval(() => {
      setCurrentTipIndex((prev) => (prev + 1) % LOADING_TIPS.length);
    }, 2000);
    return () => clearInterval(interval);
  }, [isApproving]);

  // Handle initial approve click - check if all todos complete, then show modal if needed
  const handleApproveClick = async () => {
    if (oldObjective && oldObjective.trim().length > 0) {
      // Check if all todos are complete in the current conversation
      try {
        const todos = await todosAPI.getTodos(conversationId);

        // Flatten all todos from all stages
        const allTodos = todos.flatMap(stage => stage.todos || []);

        // Get all non-deleted required todos
        const requiredTodos = allTodos.filter(todo => !todo.is_optional && !todo.is_deleted);

        // If there are NO previous todos at all (onboarding case), skip the modal
        if (requiredTodos.length === 0) {
          // console.log('[TodoPlanCard] No previous todos found - skipping completion modal');
          handleApprove(null);
          return;
        }

        // Check if all required todos are completed
        const allComplete = requiredTodos.every(todo => todo.is_completed);

        if (allComplete) {
          // All todos complete - automatically mark as complete, no need to ask
          // console.log('[TodoPlanCard] All todos complete - auto-marking previous mission as [complete]');
          handleApprove(true);
          return;
        }
      } catch (error) {
        console.error('[TodoPlanCard] Failed to check todos completion:', error);
        // On error, skip the modal to avoid blocking the user
        handleApprove(null);
        return;
      }

      // If we get here, there are incomplete todos - ask the user
      setShowCompletionModal(true);
    } else {
      handleApprove(null);
    }
  };

  // Handle approval with completion status
  const handleApprove = async (wasCompleted) => {
    setShowCompletionModal(false);
    setIsApproving(true);

    // Immediately continue to onboarding (don't wait for todos to be created)
    // Pass the approval function so parent can decide when to show loading
    if (onApprove) {
      onApprove(() => {
        // This callback will be called if parent needs to show loading
        // (e.g., when user finishes onboarding but todos aren't ready)
        return createTodosInBackground(wasCompleted);
      });
    } else {
      // In-chat flow: create todos then notify parent to sync state
      await createTodosInBackground(wasCompleted);
      setIsApproving(false);
    }
  };

  // Create todos in background and return a promise
  const createTodosInBackground = (wasCompleted) => {
    // Invalidate all caches since we'll have new conversation and todos
    stateManager.invalidate('messages');
    stateManager.invalidate('todos');
    stateManager.invalidate('progressData');

    // Return the promise so parent can track completion
    return todosAPI.approvePlan(
      conversationId,
      plan,
      objective,
      wasCompleted
    ).then((response) => {
      console.log('[TodoPlanCard] Todos created successfully');

      // CRITICAL: Update conversation ID with the new one from backend
      if (response.newConversationId) {
        console.log('[TodoPlanCard] Updating conversation ID:', conversationId, '→', response.newConversationId);
        stateManager.setConversationId(response.newConversationId);
      }

      // Invalidate caches AFTER setting new conversation ID
      stateManager.invalidate('messages');
      stateManager.invalidate('todos');
      stateManager.invalidate('progressData');
      setIsApproving(false);

      // Notify parent to sync React state (conversationId, mission, etc.)
      if (onApproved) onApproved();
    }).catch(error => {
      console.error('[TodoPlanCard] Error creating todos:', error);
      setIsApproving(false);
      throw error;
    });
  };

  // Handle edit - show modal to collect user's edit request
  const handleEdit = () => {
    setShowEditModal(true);
  };

  // Handle edit submission
  const handleEditSubmit = () => {
    if (editMessage.trim() && onEdit) {
      setShowEditModal(false);
      onEdit(editMessage.trim());
      setEditMessage('');
    }
  };

  return (
    <div className={styles.todoPlanCard}>
      <div className={styles.header}>
        <div className={styles.headerIcon}>
          <SparkleIcon size={32} />
        </div>
        <div className={styles.headerTitle}>YOUR PERSONALISED ACTION PLAN</div>
      </div>

      {/* Shell concerns (backdrop blur, panel, focus trap) come from Modal.
          The answer decides how the old mission is archived, so the dialog is
          non-dismissible (no onClose, no ✕, no backdrop close) — the user must
          pick "Not Yet" or "Yes, Completed!". */}
      {showCompletionModal && (
        <Modal
          isOpen
          size="md"
          labelledBy="todo-plan-completion-title"
          showClose={false}
          closeOnBackdrop={false}
        >
          <div className={styles.modalTitle} id="todo-plan-completion-title">Previous Goal Status</div>
          <div className={styles.modalMessage}>
            Did you complete your previous goal?
            <div className={styles.oldObjective}>"{oldObjective}"</div>
          </div>
          <div className={styles.modalButtons}>
            <button
              className={styles.modalBtnNo}
              onClick={() => handleApprove(false)}
            >
              Not Yet
            </button>
            <button
              className={styles.modalBtnYes}
              onClick={() => handleApprove(true)}
            >
              Yes, Completed!
            </button>
          </div>
        </Modal>
      )}

      {/* Shell concerns (backdrop blur, panel, focus trap) come from Modal.
          As before the migration, this dialog only closes through its own
          buttons ("Cancel" / "Send Changes") — no onClose, ✕ or backdrop
          close, so a stray tap can't discard a typed edit request. */}
      {showEditModal && (
        <Modal
          isOpen
          size="md"
          labelledBy="todo-plan-edit-title"
          showClose={false}
          closeOnBackdrop={false}
        >
          <div className={styles.modalTitle} id="todo-plan-edit-title">What would you like to change?</div>
          <div className={styles.modalMessage}>
            Tell me what you'd like to adjust about this plan
          </div>
          <textarea
            className={styles.editTextarea}
            value={editMessage}
            onChange={(e) => setEditMessage(e.target.value)}
            placeholder="E.g., 'Make the first stage easier' or 'Add more steps about budgeting'"
            autoFocus
            rows={4}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                handleEditSubmit();
              }
            }}
          />
          <div className={styles.modalButtons}>
            <button
              className={styles.modalBtnNo}
              onClick={() => {
                setShowEditModal(false);
                setEditMessage('');
              }}
            >
              Cancel
            </button>
            <button
              className={styles.modalBtnYes}
              onClick={handleEditSubmit}
              disabled={!editMessage.trim()}
            >
              Send Changes
            </button>
          </div>
        </Modal>
      )}

      {isApproving ? (
        <Loader
          variant="overlay"
          text={LOADING_TIPS[currentTipIndex]}
          estimatedMs={APPROVE_ESTIMATE_MS}
        />
      ) : (
        <>
          <div className={styles.scrollContainer}>
            {plan.map((stage, stageIndex) => (
              <div key={stageIndex} className={styles.stage}>
                <div className={styles.stageTitle}>
                  STAGE {stageIndex + 1}: {cleanStageTitle(stage.title)}
                </div>

                <div className={styles.todoList}>
                  {stage.todos.map((todo, todoIndex) => {
                    const isOptional = todo.trim().startsWith('*');
                    const todoText = isOptional ? todo.trim().substring(1).trim() : todo;

                    return (
                      <div key={todoIndex} className={styles.todoItem}>
                        <div className={styles.todoCheckbox}>
                          {isOptional ? '◇' : '◆'}
                        </div>
                        <div className={isOptional ? styles.todoTextOptional : styles.todoText}>
                          {todoText}
                          {isOptional && <span className={styles.optionalBadge}>OPTIONAL</span>}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>

          <div className={styles.actions}>
            <div className={styles.actionPrompt}>
              Should we make any changes to this or confirm this plan?
            </div>
            <div className={styles.actionButtons}>
              <button
                className={`${styles.editBtn} ${demoPress === 'edit' ? styles.btnPressed : ''}`}
                onClick={readOnly ? undefined : handleEdit}
                tabIndex={readOnly ? -1 : undefined}
                aria-disabled={readOnly || undefined}
              >
                Edit
              </button>
              <button
                className={`${styles.approveBtn} ${demoPress === 'accept' ? styles.btnPressed : ''}`}
                onClick={readOnly ? undefined : handleApproveClick}
                tabIndex={readOnly ? -1 : undefined}
                aria-disabled={readOnly || undefined}
              >
                Accept New Plan
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export default TodoPlanCard;
